// Apolo IDE — PdfBlockBody (sprint plan-tab-v1, E4).
//
// Render preview de PDF via <embed> nativo do browser. Click "Abrir" abre
// PdfViewerModal full-screen. Sem dep pdfjs-dist — browsers modernos têm
// PDF viewer built-in.

import { useState } from 'react';
import { FileText, Maximize2 } from 'lucide-react';
import { apiUrl, getStoredToken } from '../../../lib/api.js';
import PdfViewerModal from '../PdfViewerModal.jsx';

function pdfDownloadUrl(planId, fileId) {
  return apiUrl(`/api/plan/${encodeURIComponent(planId)}/files/${encodeURIComponent(fileId)}/download`);
}

/**
 * Browser PDF viewer requires the document body load with proper auth.
 * Since <embed src> can't add headers, we use a blob URL fetched with the
 * Bearer token. Memoize the blob URL per (planId, fileId).
 */
function useAuthedPdfBlobUrl(planId, fileId) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  // Lazy fetch on demand.
  async function fetchBlob() {
    if (url || loading || !planId || !fileId) return;
    setLoading(true);
    try {
      const token = getStoredToken();
      const res = await fetch(pdfDownloadUrl(planId, fileId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { setErr(`falha ${res.status}`); setLoading(false); return; }
      const blob = await res.blob();
      setUrl(URL.createObjectURL(blob));
    } catch (e) {
      setErr(e?.message || 'erro');
    } finally {
      setLoading(false);
    }
  }
  return { url, err, loading, fetchBlob };
}

export default function PdfBlockBody({ block, payload, accent }) {
  const planId = block?.plan_id;
  const fileId = payload?.file_id;
  const [modalOpen, setModalOpen] = useState(false);
  const { url, err, loading, fetchBlob } = useAuthedPdfBlobUrl(planId, fileId);

  // Trigger fetch when block first mounts.
  if (planId && fileId && !url && !err && !loading) fetchBlob();

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center gap-1.5 border-b border-apolo-borderSubtle/60 px-3 py-1.5 text-[10px] uppercase tracking-wide" style={{ color: accent }}>
        <FileText size={11} aria-hidden="true" />
        <span className="truncate">{payload?.name || 'PDF'}</span>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={!url}
          aria-label="Abrir em tela cheia"
          title="Abrir PDF em tela cheia"
          className="ml-auto inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:opacity-40"
        >
          <Maximize2 size={10} /> abrir
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden bg-apolo-surface2/40">
        {loading ? (
          <div className="flex h-full items-center justify-center text-[11px] text-apolo-textTertiary">Carregando PDF…</div>
        ) : err ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <FileText size={20} className="text-apolo-textTertiary" aria-hidden="true" />
            <span className="text-[11px] text-apolo-error">{err}</span>
          </div>
        ) : url ? (
          <embed
            src={url + '#zoom=page-width&toolbar=0&navpanes=0'}
            type="application/pdf"
            aria-label={`Preview de ${payload?.name || 'PDF'}`}
            className="h-full w-full"
          />
        ) : !fileId ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <FileText size={20} className="text-apolo-textTertiary" aria-hidden="true" />
            <span className="text-[11px] text-apolo-textTertiary">Sem arquivo. Arraste um PDF aqui ou use upload no menu.</span>
          </div>
        ) : null}
      </div>
      <PdfViewerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        url={url}
        name={payload?.name || 'PDF'}
      />
    </div>
  );
}
